import React, { useMemo, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import ChartRenderer from './ChartRenderer';

interface MarkdownWithChartsProps {
  content: string;
}

const MarkdownWithCharts: React.FC<MarkdownWithChartsProps> = memo(({ content }) => {
  // Parse Chart.js configurations from the content - memoized to prevent re-parsing on every render
  const { processedContent, charts } = useMemo(() => {
    const chartRegex = /<!--CHART_JS_CONFIG_START-->([\s\S]*?)<!--CHART_JS_CONFIG_END-->/g;
    const charts: Array<{ config: any; type: string; index: number }> = [];
    let match;
    let processedContent = content;

    while ((match = chartRegex.exec(content)) !== null) {
      try {
        const configText = match[1].trim();
        const config = JSON.parse(configText);
        
        // Determine chart type from config or default to 'line'
        const chartType = config.type || 'line';
        
        // Validate chart type
        if (!['line', 'bar', 'pie'].includes(chartType)) {
          if (process.env.NODE_ENV === 'development') console.warn(`Unsupported chart type: ${chartType}. Defaulting to 'line'.`);
        }
        
        // Validate data structure
        if (!config.data || !config.data.labels || !config.data.datasets) {
          if (process.env.NODE_ENV === 'development') console.warn('Invalid chart configuration: missing data, labels, or datasets');
          continue;
        }
        
        // Limit data points to 50 as specified
        if (config.data.labels.length > 50) {
          config.data.labels = config.data.labels.slice(0, 50);
          config.data.datasets = config.data.datasets.map((dataset: any) => ({
            ...dataset,
            data: dataset.data ? dataset.data.slice(0, 50) : []
          }));
        }
        
        charts.push({
          config,
          type: chartType,
          index: charts.length
        });
        
        // Replace the chart config with a placeholder
        processedContent = processedContent.replace(
          match[0],
          `<div data-chart-placeholder="${charts.length - 1}"></div>`
        );
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Error parsing chart configuration:', error);
        // Replace with error message
        processedContent = processedContent.replace(
          match[0],
          '<div style="color: red; padding: 16px; border: 1px solid red; border-radius: 8px; margin: 16px 0;">⚠️ Error parsing chart configuration</div>'
        );
      }
    }

    return { processedContent, charts };
  }, [content]);

  const components = useMemo(() => ({
    // Custom component to render chart placeholders
    div: ({ node, ...props }: any) => {
      const chartPlaceholder = props['data-chart-placeholder'];
      if (chartPlaceholder !== undefined) {
        const chartIndex = parseInt(chartPlaceholder, 10);
        const chart = charts[chartIndex];
        if (chart) {
          return (
            <ChartRenderer
              config={chart.config}
              type={chart.type as 'line' | 'bar' | 'pie'}
            />
          );
        }
      }
      return <div {...props} />;
    },
    // Style other markdown elements
    h1: ({ node, ...props }: any) => (
      <h1 
        style={{ 
          fontSize: '24px',
          fontWeight: '700',
          color: '#18181b',
          marginBottom: '16px',
          marginTop: '24px'
        }} 
        {...props} 
      />
    ),
    h2: ({ node, ...props }: any) => (
      <h2 
        style={{ 
          fontSize: '20px',
          fontWeight: '600',
          color: '#18181b',
          marginBottom: '12px',
          marginTop: '20px'
        }} 
        {...props} 
      />
    ),
    h3: ({ node, ...props }: any) => (
      <h3 
        style={{ 
          fontSize: '18px',
          fontWeight: '600',
          color: '#18181b',
          marginBottom: '8px',
          marginTop: '16px'
        }} 
        {...props} 
      />
    ),
    p: ({ node, ...props }: any) => (
      <p 
        style={{ 
          fontSize: '16px',
          fontWeight: '400',
          color: '#52525b',
          lineHeight: '24px',
          marginBottom: '12px'
        }} 
        {...props} 
      />
    ),
    ul: ({ node, ...props }: any) => (
      <ul 
        style={{ 
          paddingLeft: '20px',
          marginBottom: '12px'
        }} 
        {...props} 
      />
    ),
    ol: ({ node, ...props }: any) => (
      <ol 
        style={{ 
          paddingLeft: '20px',
          marginBottom: '12px'
        }} 
        {...props} 
      />
    ),
    li: ({ node, ...props }: any) => (
      <li 
        style={{ 
          fontSize: '16px',
          fontWeight: '400',
          color: '#52525b',
          lineHeight: '24px',
          marginBottom: '4px'
        }} 
        {...props} 
      />
    ),
    code: ({ node, inline, ...props }: any) => (
      <code 
        style={{ 
          backgroundColor: '#f4f4f5',
          color: '#18181b',
          border: '1px solid #e4e4e7',
          padding: inline ? '2px 4px' : '12px',
          borderRadius: '4px',
          fontSize: '14px',
          fontFamily: 'monospace',
          display: inline ? 'inline' : 'block',
          marginBottom: inline ? '0' : '12px',
          whiteSpace: 'pre-wrap'
        }} 
        {...props} 
      />
    ),
    pre: ({ node, ...props }: any) => (
      <pre 
        style={{ 
          backgroundColor: '#f4f4f5',
          color: '#18181b',
          border: '1px solid #e4e4e7',
          padding: '12px',
          borderRadius: '8px',
          fontSize: '14px',
          fontFamily: 'monospace',
          marginBottom: '12px',
          whiteSpace: 'pre-wrap',
          overflow: 'auto'
        }} 
        {...props} 
      />
    ),
    blockquote: ({ node, ...props }: any) => (
      <blockquote 
        style={{ 
          borderLeft: '4px solid #e4e4e7',
          paddingLeft: '16px',
          marginLeft: '0',
          marginRight: '0',
          marginBottom: '12px',
          fontStyle: 'italic',
          color: '#52525b'
        }} 
        {...props} 
      />
    ),
    table: ({ node, ...props }: any) => (
      <div style={{ overflowX: 'auto', marginBottom: '12px' }}>
        <table 
          style={{ 
            width: '100%',
            borderCollapse: 'collapse',
            border: '1px solid #e4e4e7'
          }} 
          {...props} 
        />
      </div>
    ),
    th: ({ node, ...props }: any) => (
      <th 
        style={{ 
          padding: '8px 12px',
          backgroundColor: '#f4f4f5',
          border: '1px solid #e4e4e7',
          fontSize: '14px',
          fontWeight: '600',
          textAlign: 'left'
        }} 
        {...props} 
      />
    ),
    td: ({ node, ...props }: any) => (
      <td 
        style={{ 
          padding: '8px 12px',
          border: '1px solid #e4e4e7',
          fontSize: '14px'
        }} 
        {...props} 
      />
    ),
  }), [charts]);

  return (
    <div style={{ fontSize: '16px', lineHeight: '24px' }}>
      <ReactMarkdown
        rehypePlugins={[rehypeRaw]}
        components={components}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
});

export default MarkdownWithCharts;